import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import './AutoMenuNavigator.scss'

function removeEmoji(text) {
  return String(text).replace(/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|\uD83C[\uDE01-\uDEFF]|\uD83D[\uDC00-\uDE4F]|\uD83E[\uDD10-\uDDFF]/gu, '');
}

// Infer section from category name if not explicitly set
function inferSection(categoryName) {
  const name = (categoryName || '').toLowerCase();
  if (/drink|beverage/i.test(name)) return 'Drinks';
  if (/dessert/i.test(name)) return 'Dessert';
  if (/catering|tray|party/i.test(name)) return 'Catering';
  return 'Food';
}

// Group subMenus by section, sorted by first appearance in menu
function groupBySection(subMenus) {
  const grouped = {};
  const sectionFirstIndex = {}; // Track when each section first appears
  
  subMenus.forEach((menu, index) => {
    const section = menu.section || inferSection(menu.name?.en);
    if (!grouped[section]) {
      grouped[section] = [];
      sectionFirstIndex[section] = index; // Record first appearance
    }
    grouped[section].push(menu);
  });
  
  // Sort sections by their first category's position in the original menu
  const sortedSectionNames = Object.keys(grouped).sort((a, b) => {
    return sectionFirstIndex[a] - sectionFirstIndex[b];
  });
  
  // Build sorted result
  const sortedSections = {};
  sortedSectionNames.forEach(section => {
    sortedSections[section] = grouped[section];
  });
  
  return sortedSections;
}

// Custom hook for debounced value
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
}

// Search icon component
function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/>
      <path d="m21 21-4.35-4.35"/>
    </svg>
  );
}

// Close icon component
function CloseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18"/>
      <path d="m6 6 12 12"/>
    </svg>
  );
}

// Menu icon component
function MenuIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" x2="20" y1="12" y2="12"/>
      <line x1="4" x2="20" y1="6" y2="6"/>
      <line x1="4" x2="20" y1="18" y2="18"/>
    </svg>
  );
}

// Chevron icon component
function ChevronIcon({ expanded }) {
  return (
    <svg 
      width="16" 
      height="16" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      className={`chevron-icon ${expanded ? 'expanded' : ''}`}
    >
      <path d="m6 9 6 6 6-6"/>
    </svg>
  );
}

export default function AutoMenuNavigator({ subMenus, darkMode, onSearch, searchQueryValue }) {
  const [currentCategoryId, setCurrentCategoryId] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [expandedSections, setExpandedSections] = useState({})
  const [searchQuery, setSearchQuery] = useState('')
  const observerRef = useRef(null)
  const drawerRef = useRef(null)
  
  const debouncedQuery = useDebounce(searchQuery, 300);
  
  // Filter visible subMenus (exclude hidden)
  const visibleSubMenus = useMemo(() => {
    return subMenus.filter(({ tag }) => {
      if (tag && tag.indexOf('hide') !== -1) return false;
      return true;
    });
  }, [subMenus]);
  
  // Group menus by section
  const sections = useMemo(() => {
    return groupBySection(visibleSubMenus);
  }, [visibleSubMenus]);
  
  // Initialize only first section as expanded
  useEffect(() => {
    const sectionKeys = Object.keys(sections);
    const initialExpanded = {};
    sectionKeys.forEach((section, index) => {
      initialExpanded[section] = index === 0; // Only first section expanded
    });
    setExpandedSections(initialExpanded);
  }, [Object.keys(sections).join(',')]);
  
  // Notify parent of search query changes
  useEffect(() => {
    if (onSearch) {
      onSearch(debouncedQuery);
    }
  }, [debouncedQuery, onSearch]);

  // Sync external search value (e.g., when reset is triggered from parent)
  useEffect(() => {
    if (typeof searchQueryValue === 'string') {
      setSearchQuery(searchQueryValue);
    }
  }, [searchQueryValue]);
  
  // Click handler to scroll to category
  const clickLink = useCallback((catId) => {
    // Update hash so reload/share preserves target; AutoMenu handles scrolling
    window.location.hash = `menu-category-${catId}`;
    setDrawerOpen(false);
  }, []);
  
  // Set up intersection observer for active category tracking
  const observeNav = useCallback(() => {
    const ids = visibleSubMenus.map(({ id }) => `#menu-category-${id}`);
    
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    observerRef.current = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setCurrentCategoryId(entry.target.id);
        }
      });
    }, {
      rootMargin: "0px 0px -400px 0px"
    });
    
    ids.forEach(id => {
      const element = document.querySelector(id);
      if (element) {
        observerRef.current.observe(element);
      }
    });
  }, [visibleSubMenus]);
  
  useEffect(() => {
    observeNav();
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [observeNav]);
  
  // Close drawer on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && drawerOpen) {
        setDrawerOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [drawerOpen]);
  
  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);
  
  // Toggle section expansion - only one section open at a time
  const toggleSection = (section) => {
    setExpandedSections(prev => {
      const isCurrentlyExpanded = prev[section];
      // Collapse all sections, then toggle the clicked one
      const allCollapsed = Object.keys(prev).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {});
      return {
        ...allCollapsed,
        [section]: !isCurrentlyExpanded
      };
    });
  };
  
  // Handle keyboard navigation for section headers
  const handleSectionKeyDown = (e, section) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleSection(section);
    }
  };
  
  // Get current category name from ID
  const currentCategoryName = useMemo(() => {
    if (!currentCategoryId) return 'Menu';
    const category = visibleSubMenus.find(
      ({ id }) => `menu-category-${id}` === currentCategoryId
    );
    return category?.name?.en ? removeEmoji(category.name.en) : 'Menu';
  }, [currentCategoryId, visibleSubMenus]);
  
  // Render navigation content (used in both sidebar and drawer)
  const renderNavContent = () => (
    <>
      {/* Sections with categories */}
      <div className="menu-nav-sections">
        {Object.entries(sections).map(([sectionName, categories]) => (
          <div key={sectionName} className="menu-nav-section">
            <button
              className="section-header"
              onClick={() => toggleSection(sectionName)}
              onKeyDown={(e) => handleSectionKeyDown(e, sectionName)}
              aria-expanded={expandedSections[sectionName]}
              aria-controls={`section-${sectionName}`}
            >
              <span className="section-name">{sectionName}</span>
              <ChevronIcon expanded={expandedSections[sectionName]} />
            </button>
            
            <div 
              id={`section-${sectionName}`}
              className={`section-categories ${expandedSections[sectionName] ? 'expanded' : 'collapsed'}`}
            >
              {categories.map(({ id, name }) => {
                const isActive = `menu-category-${id}` === currentCategoryId;
                return (
                  <a
                    key={id}
                    id={`top-nav-menu-category-${id}`}
                    href={`#menu-category-${id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      clickLink(id);
                    }}
                    className={`category-link-item ${isActive ? 'active' : ''}`}
                  >
                    <span className="category-link-text">
                      {removeEmoji(name && name.en)}
                    </span>
                  </a>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </>
  );
  
  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`menu-sidebar ${darkMode ? 'dark-mode' : ''}`}>
        <nav role="navigation" aria-label="Menu Categories">
          {renderNavContent()}
        </nav>
      </aside>
      
      {/* Top Bar with Search */}
      <div className={`menu-mobile-topbar ${darkMode ? 'dark-mode' : ''} ${drawerOpen ? 'drawer-open' : ''}`}>
        {/* Search Field */}
        <div className="topbar-search">
          <SearchIcon />
          <input
            type="text"
            placeholder="Search menu..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Search menu items"
          />
          {searchQuery && (
            <button 
              className="clear-search"
              onClick={(e) => {
                e.stopPropagation();
                setSearchQuery('');
              }}
              aria-label="Clear search"
            >
              <CloseIcon />
            </button>
          )}
        </div>
        
        {/* Category Selector - hidden on desktop */}
        <div 
          className="topbar-category-selector"
          onClick={() => setDrawerOpen(true)}
          role="button"
          tabIndex={0}
          aria-label="Open menu navigation"
          aria-expanded={drawerOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setDrawerOpen(true);
            }
          }}
        >
          <span className="view-categories-text">View categories</span>
        </div>
      </div>
      
      {/* Mobile Drawer Backdrop */}
      {drawerOpen && (
        <div 
          className="menu-drawer-backdrop"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}
      
      {/* Mobile Drawer */}
      <div 
        ref={drawerRef}
        className={`menu-drawer ${drawerOpen ? 'open' : ''} ${darkMode ? 'dark-mode' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu navigation"
      >
        <div className="drawer-header">
          <h2>Menu</h2>
          <button
            className="drawer-close"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu navigation"
          >
            <CloseIcon />
          </button>
        </div>
        <nav role="navigation" aria-label="Menu Categories">
          {renderNavContent()}
        </nav>
      </div>
    </>
  );
}
